<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { GlobeIcon } from '@heroicons/vue/solid';
import countryCodes from 'country-codes-list';
import SalesTax from 'sales-tax';
import type { PropType } from 'vue';

const props = defineProps({
  modelValue: Boolean as PropType<boolean>,
});
const emit = defineEmits(['update:modelValue', 'onSave']);

const countries = countryCodes.all().sort((a, b) => a.countryNameEn.localeCompare(b.countryNameEn));
const selectedCountry = ref<string | undefined>(undefined);

const submissionError = ref<string | undefined>(undefined);

const taxRate = ref<any>(undefined);
watch(selectedCountry, async () => {
  if (selectedCountry.value) {
    SalesTax.setTaxOriginCountry('DE', false);
    const salesTax = await SalesTax.getSalesTax(selectedCountry.value);
    taxRate.value = salesTax.rate;
  } else {
    taxRate.value = undefined;
  }
});

const submit = async () => {
  submissionError.value = undefined;

  if (!selectedCountry.value || taxRate.value === undefined) {
    submissionError.value = 'Country is required.';
    return;
  }

  emit('onSave', { country: selectedCountry.value, taxRate: taxRate.value });
  emit('update:modelValue', false);
};
</script>
<template>
  <TransitionRoot as="template" :show="props.modelValue">
    <Dialog as="div" class="relative z-50" @close="emit('update:modelValue', false)">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="bg-opacity-75 fixed inset-0 bg-neutral-500/70 transition-opacity dark:bg-neutral-400/70" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all dark:bg-black sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
            >
              <div>
                <div class="sm:flex sm:items-start">
                  <div
                    class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 sm:mx-0 sm:h-10 sm:w-10"
                  >
                    <GlobeIcon class="h-6 w-6 text-emerald-600" aria-hidden="true" />
                  </div>
                  <div class="mt-3 flex flex-col gap-2 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle as="h3" class="text-lg font-medium leading-6 text-neutral-900 dark:text-neutral-50">
                      Country of Residence
                    </DialogTitle>
                    <div>
                      <p class="text-sm text-neutral-500 dark:text-neutral-400">
                        Please select your country of residence (for tax purposes).
                      </p>
                    </div>
                    <div class="mt-2">
                      <label for="profile" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200"
                        >Country</label
                      >
                      <select
                        id="location"
                        v-model="selectedCountry"
                        name="location"
                        class="mt-1 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-neutral-900 ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-emerald-600 dark:bg-neutral-800 dark:text-neutral-50 dark:text-white dark:ring-neutral-600 sm:text-sm sm:leading-6"
                      >
                        <option value="" disabled selected>Country of Residence</option>
                        <option v-for="country in countries" :value="country.countryCode">
                          {{ country.countryNameEn }}
                        </option>
                      </select>
                    </div>
                    <div v-if="submissionError">
                      <div class="text-sm font-medium text-red-600">{{ submissionError }}</div>
                    </div>
                  </div>
                </div>
                <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    class="inline-flex w-full justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-emerald-700 dark:text-black sm:ml-3 sm:w-auto sm:text-sm"
                    @click="submit"
                  >
                    Save
                  </button>
                  <button
                    ref="cancelButtonRef"
                    type="button"
                    class="mt-3 inline-flex w-full justify-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-base font-medium text-neutral-700 shadow-sm hover:bg-neutral-50 dark:bg-black dark:text-neutral-200 dark:hover:bg-neutral-900 sm:mt-0 sm:w-auto sm:text-sm"
                    @click="emit('update:modelValue', false)"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
